<script>
import { mapGetters } from 'vuex';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import BackButton from '../BackButton.vue';
import inboxMixin from 'shared/mixins/inboxMixin';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Thumbnail from '../Thumbnail.vue';
import SlaCountdownBadge from './components/SlaCountdownBadge.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURLForRoute } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import Linear from './linear/index.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { createKeyboardEventsFromLabel } from 'dashboard/components/widgets/modal/constants';

export default {
  components: {
    BackButton,
    InboxName,
    MoreActions,
    Thumbnail,
    SlaCountdownBadge,
    Linear,
    NextButton,
  },
  mixins: [inboxMixin],
  props: {
    chat: {
      type: Object,
      default: () => {},
    },
    isContactPanelOpen: {
      type: Boolean,
      default: false,
    },
    showBackButton: {
      type: Boolean,
      default: false,
    },
    isInboxView: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['contactPanelToggle'],
  setup(props, { emit }) {
    const keyboardEvents = {
      ...createKeyboardEventsFromLabel('TOGGLE_SIDEBAR', {
        action: () => emit('contactPanelToggle'),
      }),
    };
    useKeyboardEvents(keyboardEvents);
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      appIntegrations: 'integrations/getAppIntegrations',
    }),
    statusConfig() {
      const statusMap = {
        [wootConstants.STATUS_TYPE.OPEN]: {
          icon: 'mail-opened',
          label: this.$t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.open.TEXT'),
        },
        [wootConstants.STATUS_TYPE.RESOLVED]: {
          icon: 'mail-thin',
          label: this.$t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT'),
        },
        [wootConstants.STATUS_TYPE.PENDING]: {
          icon: 'robot',
          label: this.$t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.pending.TEXT'),
        },
        [wootConstants.STATUS_TYPE.SNOOZED]: {
          icon: 'alarm-snooze',
          label: this.$t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.snoozed.TEXT'),
        },
      };
      return (
        statusMap[this.currentChat.status] ||
        statusMap[wootConstants.STATUS_TYPE.OPEN]
      );
    },
    chatMetadata() {
      return this.chat.meta;
    },
    backButtonUrl() {
      return conversationListPageURLForRoute(this.$route);
    },
    isHMACVerified() {
      if (!this.isAWebWidgetInbox) {
        return true;
      }
      return this.chatMetadata.hmac_verified;
    },
    currentContact() {
      return this.$store.getters['contacts/getContact'](
        this.chat.meta.sender.id
      );
    },
    isSnoozed() {
      return this.currentChat.status === wootConstants.STATUS_TYPE.SNOOZED;
    },
    snoozedDisplayText() {
      const { snoozed_until: snoozedUntil } = this.currentChat;
      if (snoozedUntil) {
        return `${this.$t(
          'CONVERSATION.HEADER.SNOOZED_UNTIL'
        )} ${snoozedReopenTime(snoozedUntil)}`;
      }
      return this.$t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
    },
    contactPanelToggleText() {
      return `${
        this.isContactPanelOpen
          ? this.$t('CONVERSATION.HEADER.CLOSE')
          : this.$t('CONVERSATION.HEADER.OPEN')
      } ${this.$t('CONVERSATION.HEADER.DETAILS')}`;
    },
    inbox() {
      const { inbox_id: inboxId } = this.chat;
      return this.$store.getters['inboxes/getInbox'](inboxId);
    },
    hasMultipleInboxes() {
      return this.$store.getters['inboxes/getInboxes'].length > 1;
    },
    conversationSlaPolicies() {
      return this.chat?.sla_policy || [];
    },
    hasSlaPolicies() {
      return this.conversationSlaPolicies.length > 0;
    },
    isLinearIntegrationEnabled() {
      return this.appIntegrations.find(
        integration => integration.id === 'linear' && !!integration.hooks.length
      );
    },
    isLinearFeatureEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.LINEAR
      );
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col items-center justify-between px-4 py-2 border-b bg-n-background border-n-weak md:flex-row"
  >
    <div
      class="flex flex-col items-center justify-center flex-1 w-full min-w-0"
      :class="isInboxView ? 'sm:flex-row' : 'md:flex-row'"
    >
      <div class="flex items-center justify-start max-w-full min-w-0 w-fit">
        <BackButton
          v-if="showBackButton"
          :back-url="backButtonUrl"
          class="ltr:mr-2 rtl:ml-2"
        />
        <Thumbnail
          :src="currentContact.thumbnail"
          :badge="inboxBadge"
          :username="currentContact.name"
          :status="currentContact.availability_status"
        />
        <div
          class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2 w-fit"
        >
          <div
            class="flex flex-row items-center max-w-[20rem] gap-1 p-0 m-0 w-fit"
          >
            <NextButton link slate @click.prevent="$emit('contactPanelToggle')">
              <span
                data-testid="conversation-header-name"
                class="text-base font-medium truncate leading-tight text-n-slate-12"
              >
                {{ currentContact.name }}
              </span>
            </NextButton>
            <fluent-icon
              v-if="!isHMACVerified"
              v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
              size="14"
              class="text-n-amber-10 my-0 mx-0 min-w-[14px]"
              icon="warning"
            />
          </div>

          <div
            class="flex items-center gap-2 overflow-hidden text-xs conversation--header--actions text-ellipsis whitespace-nowrap"
          >
            <div
              class="inline-flex items-center py-0.5 px-0 leading-3 whitespace-nowrap bg-none text-n-slate-11 text-xs"
            >
              <fluent-icon
                :icon="statusConfig.icon"
                size="14"
                icon-lib="tabler"
                class="mr-0.5 rtl:ml-0.5 rtl:mr-0"
              />
              <span>{{ statusConfig.label }}</span>
            </div>
            <InboxName v-if="hasMultipleInboxes" :inbox="inbox" />
            <span v-if="isSnoozed" class="font-medium text-n-amber-10">
              {{ snoozedDisplayText }}
            </span>
            <NextButton
              link
              xs
              blue
              :label="contactPanelToggleText"
              class="max-md:hidden"
              @click="$emit('contactPanelToggle')"
            />
          </div>
        </div>
      </div>
      <div
        class="flex flex-row items-center justify-end flex-grow gap-2 mt-3 header-actions-wrap lg:mt-0"
        :class="[
          { 'justify-end': isContactPanelOpen },
          isInboxView ? 'w-full sm:w-auto' : 'w-full md:w-auto',
        ]"
      >
        <Linear
          v-if="isLinearIntegrationEnabled && isLinearFeatureEnabled"
          :conversation-id="currentChat.id"
        />
        <SlaCountdownBadge
          v-if="hasSlaPolicies"
          :sla-policies="conversationSlaPolicies"
          :priority="chat.priority"
          :status="currentChat.status"
          class="text-sm"
        />
        <MoreActions :conversation-id="currentChat.id" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.conversation--header--actions {
  ::v-deep .inbox--name {
    @apply m-0;
  }
}
</style>
